import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, Send } from "lucide-react";
import Navbar from "@/components/Navbar";
import TrialBanner from "@/components/TrialBanner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEmployer, isAgency } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CITIES = {
  "San Francisco, CA": [37.7749, -122.4194],
  "New York, NY": [40.7128, -74.006],
  "Austin, TX": [30.2672, -97.7431],
  "Seattle, WA": [47.6062, -122.3321],
};

export default function PostJob() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({
    title: "", company: user?.name || "", city: "San Francisco, CA",
    job_type: "Full-time", work_mode: "Hybrid", category: "Engineering", experience: "Mid",
    salary: [120000, 180000], description: "", skills: "", urgent: false,
  });
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const agency = isAgency(user?.role);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  React.useEffect(() => {
    if (agency) api.get("/agency/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [agency]);

  if (user && !isEmployer(user.role)) {
    return (
      <div className="min-h-screen wo-bg"><Navbar />
        <div className="max-w-lg mx-auto px-5 py-24 text-center">
          <p className="text-slate-400">Only company & agency accounts can post jobs.</p>
          <Button onClick={() => navigate("/jobs")} className="mt-5 bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">Browse jobs instead</Button>
        </div>
      </div>
    );
  }

  const submit = async () => {
    setSaving(true);
    const [lat, lng] = CITIES[f.city];
    const client = clients.find((c) => c.id === clientId);
    try {
      await api.post("/jobs", {
        title: f.title, company: agency && client ? client.name : f.company, location: f.city, lat, lng,
        job_type: f.job_type, work_mode: f.work_mode, category: f.category, experience: f.experience,
        salary_min: f.salary[0], salary_max: f.salary[1], description: f.description,
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean), urgent: f.urgent,
        client_id: agency && client ? client.id : null, client_name: agency && client ? client.name : null,
      });
      toast.success("Job posted!");
      navigate("/applicants");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sel = (key, opts, tid) => (
    <div>
      <Label className="text-slate-300 text-sm">{key.replace("_", " ")}</Label>
      <Select value={f[key]} onValueChange={(v) => set(key, v)}>
        <SelectTrigger data-testid={tid} className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-200 capitalize"><SelectValue /></SelectTrigger>
        <SelectContent className="glass border-slate-700 text-slate-200">
          {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-3 mb-6"><PlusCircle className="text-teal-400" /> Post a Job</h1>
        <TrialBanner />
        <div className="liquid-glass rounded-2xl p-6 sm:p-8 space-y-5">
          <div>
            <Label className="text-slate-300 text-sm">Job title</Label>
            <Input data-testid="post-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Senior Frontend Engineer" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
          </div>
          {agency && (
            <div>
              <Label className="text-slate-300 text-sm">Hiring for client</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="post-client" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-200"><SelectValue placeholder="Select a client company…" /></SelectTrigger>
                <SelectContent className="glass border-slate-700 text-slate-200">
                  {clients.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No clients yet — add one on the Clients page</div>}
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">The posting will appear under this client's company name.</p>
            </div>
          )}
          {!agency && (
            <div>
              <Label className="text-slate-300 text-sm">Company</Label>
              <Input data-testid="post-company" value={f.company} onChange={(e) => set("company", e.target.value)} className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
            </div>
          )}
          <div>
            <Label className="text-slate-300 text-sm">Location (radar pin)</Label>
            <Select value={f.city} onValueChange={(v) => set("city", v)}>
              <SelectTrigger data-testid="post-city" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-slate-700 text-slate-200">
                {Object.keys(CITIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {sel("job_type", ["Full-time", "Part-time", "Contract", "Internship", "Freelance"], "post-job-type")}
            {sel("work_mode", ["Remote", "Onsite", "Hybrid"], "post-work-mode")}
            {sel("category", ["Engineering", "Design", "Product", "Marketing", "Data", "Sales", "Operations"], "post-category")}
            {sel("experience", ["Entry", "Mid", "Senior", "Lead", "Executive"], "post-experience")}
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Salary range: <span className="text-teal-400 font-mono">${Math.round(f.salary[0]/1000)}k – ${Math.round(f.salary[1]/1000)}k</span></Label>
            <Slider data-testid="post-salary" value={f.salary} onValueChange={(v) => set("salary", v)} min={30000} max={350000} step={5000} className="mt-3" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Description</Label>
            <Textarea data-testid="post-description" value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100 min-h-[110px]" placeholder="Describe the role..." />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Skills (comma separated)</Label>
            <Input data-testid="post-skills" value={f.skills} onChange={(e) => set("skills", e.target.value)} placeholder="React, TypeScript, GraphQL" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-100" />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-slate-950/50 border border-slate-700 px-4 py-3">
            <span className="text-sm text-slate-300">Mark as urgent</span>
            <Switch data-testid="post-urgent" checked={f.urgent} onCheckedChange={(v) => set("urgent", v)} />
          </div>
          <Button data-testid="employer-post-job-button" onClick={submit} disabled={saving || !f.title || (user?.trial?.on_trial && !user.trial.active)}
            className="w-full bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 py-6 disabled:opacity-50">
            <Send size={16} className="mr-2" /> {saving ? "Posting..." : "Publish job"}
          </Button>
        </div>
      </div>
    </div>
  );
}
